import React from 'react';
import { Grid, Package, Ruler, PlusSquare, DollarSign } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета плитки
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета плитки">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Количество плитки */}
        <div className="rounded-lg bg-gradient-to-br from-fuchsia-50 to-purple-50 dark:from-fuchsia-900/30 dark:to-purple-900/30 p-5 border border-fuchsia-100 dark:border-fuchsia-800/50 shadow-sm">
          <h3 className="text-base font-medium text-fuchsia-700 dark:text-fuchsia-300 mb-1.5 flex items-center gap-1.5">
            <Grid className="w-4 h-4"/>
            Необходимое количество плитки
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-fuchsia-800 dark:text-fuchsia-200" aria-live="polite">
              {formatNumber(results.totalTilesNeeded)} шт.
            </span>
            <span className="text-lg font-medium text-fuchsia-600 dark:text-fuchsia-400">
              ({formatNumber(results.packagesNeeded)} уп.)
            </span>
          </div>
          <p className="text-xs text-fuchsia-600 dark:text-fuchsia-400 mt-1">
            Площадь: {formatNumber(results.totalTileArea, 2)} м²
            {results.wasteFactor > 0 && ` (включая запас ${formatNumber(results.wasteFactor, 0)}%)`}
          </p>
        </div>

        {/* Общая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-cyan-50 dark:from-indigo-900/30 dark:to-cyan-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Общая стоимость материалов
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.totalCost, 0, 'ru-RU')}
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {formatCurrency(results.totalCost / results.totalTileArea, 0, 'ru-RU')} за м²
            {results.laborCost > 0 && ` (включая работу: ${formatCurrency(results.laborCost, 0, 'ru-RU')})`}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Общая площадь облицовки" 
          value={`${formatNumber(results.totalArea, 2)} м²`} 
          color="fuchsia" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        {results.floorArea > 0 && (
          <ResultItem 
            label="Площадь пола" 
            value={`${formatNumber(results.floorArea, 2)} м²`} 
            color="indigo" 
            icon={<Ruler className="w-4 h-4"/>}
          />
        )}
        
        {results.wallArea > 0 && (
          <ResultItem 
            label="Площадь стен" 
            value={`${formatNumber(results.wallArea, 2)} м²`} 
            color="indigo" 
            icon={<Ruler className="w-4 h-4"/>}
          />
        )}
        
        <ResultItem 
          label="Площадь с запасом" 
          value={`${formatNumber(results.totalAreaWithWaste, 2)} м² (запас ${formatNumber(results.wasteFactor, 0)}%)`} 
          color="green" 
        />
        
        <ResultItem 
          label="Плитка" 
          value={`${formatNumber(results.totalTilesNeeded)} шт. (${formatNumber(results.packagesNeeded)} уп.)`} 
          color="fuchsia" 
          icon={<Grid className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Стоимость плитки" 
          value={formatCurrency(results.totalTileCost, 0, 'ru-RU')} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>} 
        />
        
        {results.adhesiveNeeded > 0 && (
          <ResultItem 
            label="Плиточный клей" 
            value={`${formatNumber(results.adhesiveNeeded, 1)} кг (${results.adhesivePackages} мешков)`} 
            color="gray" 
            icon={<PlusSquare className="w-4 h-4"/>} 
          />
        )}
        
        {results.groutNeeded > 0 && (
          <ResultItem 
            label="Затирка для швов" 
            value={`${formatNumber(results.groutNeeded, 1)} кг (${results.groutPackages} уп.)`} 
            color="gray" 
            icon={<PlusSquare className="w-4 h-4"/>} 
          />
        )}
        
        {results.primerNeeded > 0 && (
          <ResultItem 
            label="Грунтовка" 
            value={`${formatNumber(results.primerNeeded, 1)} л (${results.primerPackages} шт.)`} 
            color="gray" 
            icon={<PlusSquare className="w-4 h-4"/>} 
          />
        )}
        
        {results.laborCost > 0 && (
          <ResultItem 
            label="Стоимость работ" 
            value={formatCurrency(results.laborCost, 0, 'ru-RU')} 
            color="indigo" 
            icon={<DollarSign className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Общая стоимость" 
          value={formatCurrency(results.totalCost, 0, 'ru-RU')} 
          color="indigo" 
          icon={<DollarSign className="w-4 h-4"/>} 
        />
      </div>
      
      {/* Информационный блок */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Package className="w-5 h-5 text-fuchsia-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендации по покупке</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Рекомендуется приобрести {results.packagesNeeded + (results.packagesNeeded > 10 ? 1 : (results.packagesNeeded > 5 ? 1 : 1))} упаковок плитки 
              из одной партии (тона), чтобы избежать различий в оттенке. Желательно сохранить чек и часть упаковки для возможного 
              добора материала при необходимости.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;